import en from 'lib/i18n/locales/en.json';
import vi from 'lib/i18n/locales/vi.json';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type TLanguage = 'vi' | 'en';

type TLocaleMessages = {
  [key: string]: string | TLocaleMessages;
};

type TLanguageContextValue = {
  language: TLanguage;
  setLanguage: (language: TLanguage) => void;
  t: (key: string) => string;
};

const LANGUAGE_STORAGE_KEY = 'report-language';
/** Flattens locale sections while keeping translation keys unchanged. */
const flattenTranslations = (
  messages: TLocaleMessages
): Record<string, string> =>
  Object.entries(messages).reduce<Record<string, string>>(
    (flattened, [key, value]) => {
      if (typeof value === 'string') {
        flattened[key] = value;
      } else {
        Object.assign(flattened, flattenTranslations(value));
      }
      return flattened;
    },
    {}
  );

const translations: Record<TLanguage, Record<string, string>> = {
  en: flattenTranslations(en),
  vi: flattenTranslations(vi),
};
const sourceToKey = Object.entries(translations.vi).reduce<
  Record<string, string>
>((lookup, [key, value]) => {
  if (!(value in lookup)) lookup[value] = key;
  return lookup;
}, {});

const LanguageContext = createContext<TLanguageContextValue | null>(null);

/** Reads the saved language preference, defaulting to Vietnamese. */
const getInitialLanguage = (): TLanguage => {
  if (typeof window === 'undefined') return 'vi';
  const savedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return savedLanguage === 'en' ? 'en' : 'vi';
};

type TLanguageProviderProps = {
  children: ReactNode;
};

/** Provides language selection and dictionary lookup to the application. */
export const LanguageProvider = ({ children }: TLanguageProviderProps) => {
  const [language, setLanguageState] = useState<TLanguage>(getInitialLanguage);

  const setLanguage = useCallback((nextLanguage: TLanguage) => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, nextLanguage);
    }
    setLanguageState(nextLanguage);
  }, []);

  const t = useCallback(
    (key: string) => {
      const translationKey = key in translations.vi ? key : sourceToKey[key];
      return translationKey
        ? translations[language][translationKey] || key
        : key;
    },
    [language]
  );

  const value = useMemo(
    () => ({ language, setLanguage, t }),
    [language, setLanguage, t]
  );

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
};

/** Returns the active language, its setter, and the translation lookup. */
export const useLanguage = (): TLanguageContextValue => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
